import React, { useCallback, useMemo } from 'react';
import { isDefined, sortBy } from '@nfctron/nexus-core';
import { cn, useLogger } from '@nfctron/nexus-react';

import { type SectorTypes as Types } from './Sector.types';

/**
 * Sector component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export function Sector<T extends Types.SectorTag>(props: Types.Props<T>) {
	const { sector, onSectorSelect, onSectorUnselect, getIsSector, tag, children, ...svgProps } = props;
	const { loggerLow } = useLogger('VenueMap', { medium: true, high: true });

	/** generated svg gradient stops */
	const svgGradientStops = useMemo(
		() =>
			sector?.categories.sort(sortBy('categoryId')).map((category, index, categories) => {
				const start = index === 0 ? 0 : (index / categories.length) * 100;
				const end = ((index + 1) / categories.length) * 100;
				return (
					<React.Fragment key={category.categoryId}>
						<stop offset={`${start}%`} stopColor={category.color} />
						<stop offset={`${end}%`} stopColor={category.color} />
					</React.Fragment>
				);
			}),
		[sector?.categories],
	);

	/** onClick handler */
	const onClickHandler = useCallback(() => {
		if (!isDefined(sector)) return;
		const { opened: isSelected } = getIsSector(sector.sectorId);
		if (isSelected) {
			onSectorUnselect();
		} else {
			onSectorSelect(sector.sectorId);
		}
	}, [sector, getIsSector, onSectorUnselect, onSectorSelect]);

	/** sector svg element */
	const sectorSvg = useMemo(() => {
		if (!isDefined(sector))
			return React.createElement(tag, {
				...svgProps, // Only spread SVG-valid props
				...((!isDefined(svgProps.fill) || svgProps.fill === 'none') && {
					fill: '#d4d4d8',
					stroke: '#52525b',
				}),
			});

		/** get reason why the sector is not available */
		const { soldOut, opened } = getIsSector(sector.sectorId);

		/** create sector element */
		return React.createElement(tag, {
			...svgProps, // Only spread SVG-valid props
			fill: (svgProps as any)?.dataForceFill ?? `url(#fill:${sector.sectorId})`,
			stroke: '#d4d4d8',
			strokeOpacity: 0.25,
			onClick: onClickHandler,
			className: cn(['transition-all hover:opacity-80', !soldOut && 'cursor-pointer']),
			...(soldOut && {
				fill: '#52525b',
			}),
		});
	}, [getIsSector, onClickHandler, svgProps, sector, tag]);

	/** unable to render the sector due to missing config */
	if (!isDefined(sector)) {
		loggerLow(`Unable to render sector "${props.id}" due to missing config.`);
		return sectorSvg;
	}

	return (
		<React.Fragment>
			<defs>
				<linearGradient id={`fill:${sector.sectorId}`} x1="0" x2="1" y1="0" y2="1">
					{svgGradientStops}
				</linearGradient>
				<linearGradient id={`badge:${sector.sectorId}`}>{svgGradientStops}</linearGradient>
			</defs>

			{/* sector svg */}
			{sectorSvg}
		</React.Fragment>
	);
}

/** Sector component display name */
Sector.displayName = 'component/Sector';
